import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'small';
export type AvatarShape = 'circle' | 'rounded' | 'square';
export type AvatarStatus = 'online' | 'offline' | 'away' | 'busy' | 'none';

/**
 * GAP-FE-021B · migrated from decorator @Input to signal input().
 *
 * Legacy aliases (label → name, 'small' size mapping → 'sm') flow through
 * computed signals so consumers using either spelling keep working.
 */
@Component({
  selector: 'ui-avatar',
  standalone: true,
  imports: [],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvatarComponent {
  readonly src = input<string | undefined>(undefined);
  readonly alt = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);

  /** Legacy `label` alias — falls through to `name`. */
  readonly label = input<string | undefined>(undefined);

  /**
   * Size as an input. Accepts 'small' for backward compat; effectiveSize
   * normalizes it to 'sm'.
   */
  readonly size = input<AvatarSize>('md');

  readonly shape = input<AvatarShape>('circle');
  readonly status = input<AvatarStatus>('none');
  readonly badge = input<number | undefined>(undefined);
  readonly ariaLabel = input<string | undefined>(undefined);

  imageError = false;

  /** Resolve label fallback to name. */
  readonly effectiveName = computed<string | undefined>(() =>
    this.name() ?? this.label()
  );

  /** Normalize legacy 'small' size to 'sm'. */
  readonly effectiveSize = computed<AvatarSize>(() => {
    const s = this.size();
    return s === 'small' ? 'sm' : s;
  });

  readonly initials = computed<string>(() => {
    const n = this.effectiveName();
    if (!n) return '';
    const parts = n.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  });

  readonly containerClasses = computed<string>(() => {
    const sizeClasses: Record<string, string[]> = {
      xs: ['w-6', 'h-6'],
      sm: ['w-8', 'h-8'],
      md: ['w-10', 'h-10'],
      lg: ['w-12', 'h-12'],
      xl: ['w-16', 'h-16']
    };

    const shapeClasses: Record<AvatarShape, string[]> = {
      circle: ['rounded-full'],
      rounded: ['rounded-lg'],
      square: ['rounded-none']
    };

    const baseClasses = [
      'relative',
      'inline-flex',
      'items-center',
      'justify-center',
      'overflow-hidden',
      'bg-bb-bg-tertiary',
      'text-bb-text-secondary',
      'shrink-0',
      ...sizeClasses[this.effectiveSize()],
      ...shapeClasses[this.shape()]
    ];

    return baseClasses.join(' ');
  });

  readonly initialsClasses = computed<string>(() => {
    const sizeClasses: Record<string, string[]> = {
      xs: ['text-2xs'],
      sm: ['text-xs'],
      md: ['text-sm'],
      lg: ['text-base'],
      xl: ['text-lg']
    };

    return [
      'font-semibold',
      'text-bb-text-primary',
      'select-none',
      ...sizeClasses[this.effectiveSize()]
    ].join(' ');
  });

  readonly iconClasses = computed<string>(() => {
    const sizeClasses: Record<string, string[]> = {
      xs: ['w-3', 'h-3'],
      sm: ['w-4', 'h-4'],
      md: ['w-5', 'h-5'],
      lg: ['w-6', 'h-6'],
      xl: ['w-8', 'h-8']
    };

    return sizeClasses[this.effectiveSize()].join(' ');
  });

  readonly statusClasses = computed<string>(() => {
    const statusColors: Record<AvatarStatus, string[]> = {
      online: ['bg-bb-accent-green'],
      offline: ['bg-bb-text-secondary'],
      away: ['bg-bb-accent-amber'],
      busy: ['bg-bb-accent-red'],
      none: []
    };

    const sizePositions: Record<string, string[]> = {
      xs: ['w-1.5', 'h-1.5', 'right-0', 'bottom-0'],
      sm: ['w-2', 'h-2', 'right-0', 'bottom-0'],
      md: ['w-2.5', 'h-2.5', 'right-0', 'bottom-0'],
      lg: ['w-3', 'h-3', 'right-0.5', 'bottom-0.5'],
      xl: ['w-4', 'h-4', 'right-1', 'bottom-1']
    };

    return [
      'absolute',
      'rounded-full',
      'ring-2',
      'ring-bb-bg-primary',
      ...statusColors[this.status()],
      ...sizePositions[this.effectiveSize()]
    ].join(' ');
  });

  readonly badgeClasses = computed<string>(() => {
    const sizeClasses: Record<string, string[]> = {
      xs: ['text-4xs', 'min-w-[12px]', 'h-3', 'px-0.5', '-top-1', '-right-1'],
      sm: ['text-2xs', 'min-w-[14px]', 'h-3.5', 'px-1', '-top-1', '-right-1'],
      md: ['text-xs', 'min-w-[16px]', 'h-4', 'px-1', '-top-1', '-right-1'],
      lg: ['text-xs', 'min-w-[18px]', 'h-4.5', 'px-1.5', '-top-1', '-right-1'],
      xl: ['text-sm', 'min-w-[20px]', 'h-5', 'px-1.5', '-top-1', '-right-1']
    };

    return [
      'absolute',
      'flex',
      'items-center',
      'justify-center',
      'bg-bb-accent-red',
      'text-white',
      'rounded-full',
      'font-semibold',
      'ring-2',
      'ring-bb-bg-primary',
      ...sizeClasses[this.effectiveSize()]
    ].join(' ');
  });

  onImageError(): void {
    this.imageError = true;
  }
}
